import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import axiosInstance from "../../services/api/axios";
import { toast } from "react-toastify";
import { Spinner } from "@material-tailwind/react";
import { FormModal, Field, FormSubmit, AreaPair, yupResolver } from "../form";
import { normalizePropertyConfigMediaForUI } from "../../utils/media";
import {
    propertyConfigSchema,
    PROPERTY_TYPE_OPTIONS,
} from "../../services/projects/schemas";

/** API may return camelCase or snake_case; nested `propertyConfig` appears in some payloads. */
const getPropertyConfigId = (editConfig) => {
    if (!editConfig) return undefined;
    return (
        editConfig.propertyConfigId ??
        editConfig.property_config_id ??
        editConfig.propertyConfig?.propertyConfigId ??
        editConfig.propertyConfig?.property_config_id
    );
};

const MEASUREMENT_UNITS = [
    { value: "sqft", label: "Square Feet (sq ft)" },
    { value: "sqmt", label: "Square Meters (sq mt)" },
];

// Subcomponent so the conditional "non-Shop" fields can subscribe to
// `propertyType` cheaply via useWatch — keeps the main render shallow.
const NonShopFields = () => {
    const propertyType = useWatch({ name: "propertyType" });
    if (propertyType === "Shop") return null;
    return (
        <>
            <AreaPair label="Dry Balcony Area" nameSqft="dryBalconyAreaSqft" nameSqmt="dryBalconyAreaSqmt" />
            <AreaPair label="Balcony Area" nameSqft="balconyAreaSqft" nameSqmt="balconyAreaSqmt" />
        </>
    );
};

const SizeBhkField = () => {
    const propertyType = useWatch({ name: "propertyType" });
    if (propertyType === "Shop") return null;
    return <Field name="sizeBhk" label="Size (BHK)" type="number" min={1} required />;
};

const CreateConfigModal = ({ isOpen, onClose, projectId, onCreate, editConfigData, phaseId }) => {
    const isEdit = !!editConfigData;
    const editingPropertyConfigId = getPropertyConfigId(editConfigData);

    const [currencies, setCurrencies] = useState([]);
    const [selectedImages, setSelectedImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [selectedDetailedFloorPlans, setSelectedDetailedFloorPlans] = useState([]);
    const [detailedFloorPlanPreviews, setDetailedFloorPlanPreviews] = useState([]);
    const [existingPropertyImages, setExistingPropertyImages] = useState([]);
    const [existingDetailedFloorPlans, setExistingDetailedFloorPlans] = useState([]);
    const [loadingExistingMedia, setLoadingExistingMedia] = useState(false);
    const [primaryMeasurementUnit, setPrimaryMeasurementUnit] = useState("sqft");

    const defaultValues = useMemo(
        () => ({
            configName: editConfigData?.configName || "",
            propertyType: editConfigData?.propertyType || "",
            sizeBhk: editConfigData?.sizeBhk ?? "",
            carpetAreaSqft: editConfigData?.carpetAreaSqft ?? "",
            carpetAreaSqmt: editConfigData?.carpetAreaSqmt ?? "",
            superBuiltupAreaSqft: editConfigData?.superBuiltupAreaSqft ?? "",
            superBuiltupAreaSqmt: editConfigData?.superBuiltupAreaSqmt ?? "",
            dryBalconyAreaSqft: editConfigData?.dryBalconyAreaSqft ?? "",
            dryBalconyAreaSqmt: editConfigData?.dryBalconyAreaSqmt ?? "",
            balconyAreaSqft: editConfigData?.balconyAreaSqft ?? "",
            balconyAreaSqmt: editConfigData?.balconyAreaSqmt ?? "",
            basePrice: editConfigData?.basePrice ?? "",
            currencyId: editConfigData?.currencyId ?? "",
        }),
        [editConfigData],
    );

    const methods = useForm({
        defaultValues,
        mode: "onTouched",
        resolver: yupResolver(propertyConfigSchema),
    });

    // Reset on open / on different edit target. Without this RHF holds stale
    // values when the modal is reopened against a new config.
    useEffect(() => {
        if (!isOpen) return;
        methods.reset(defaultValues);
        setSelectedImages([]);
        setSelectedDetailedFloorPlans([]);
        // Revoke any blob URLs from a prior open before clearing — the change
        // handlers no longer revoke on every pick (they append), so cleanup
        // happens here and in the per-tile remove handlers.
        setImagePreviews((prev) => {
            prev.forEach((u) => URL.revokeObjectURL(u));
            return [];
        });
        setDetailedFloorPlanPreviews((prev) => {
            prev.forEach((u) => URL.revokeObjectURL(u));
            return [];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, editConfigData]);

    // Fetch currencies once per open.
    useEffect(() => {
        if (!isOpen) return;
        (async () => {
            try {
                const resp = await axiosInstance.get("/currency");
                setCurrencies(resp || []);
            } catch (error) {
                console.error("Error fetching currencies:", error);
                toast.error("Error loading currencies");
            }
        })();
    }, [isOpen]);

    // Fetch existing media for edit mode.
    useEffect(() => {
        if (!isOpen || !editingPropertyConfigId) {
            setExistingPropertyImages([]);
            setExistingDetailedFloorPlans([]);
            return;
        }
        setLoadingExistingMedia(true);
        (async () => {
            try {
                const mediaResponse = await axiosInstance.get("/property/config/media", {
                    params: { property_config_id: editingPropertyConfigId },
                });
                const { gallery, detailedFloorPlans } =
                    normalizePropertyConfigMediaForUI(mediaResponse);
                setExistingPropertyImages(gallery || []);
                setExistingDetailedFloorPlans(detailedFloorPlans || []);
            } catch (error) {
                console.error("Error fetching existing config media:", error);
                setExistingPropertyImages([]);
                setExistingDetailedFloorPlans([]);
            } finally {
                setLoadingExistingMedia(false);
            }
        })();
    }, [isOpen, editingPropertyConfigId]);

    const currencyOptions = useMemo(
        () =>
            currencies.map((c) => ({
                value: c.currencyId,
                label: c.currencyName,
            })),
        [currencies],
    );

    const handleImageChange = (e) => {
        e.stopPropagation();
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        // Append, don't replace — picking images in separate batches should
        // accumulate. Clearing the input value afterward lets the user
        // re-select the same file later (onChange won't fire otherwise).
        setSelectedImages((prev) => [...prev, ...files]);
        setImagePreviews((prev) => [...prev, ...files.map((f) => URL.createObjectURL(f))]);
        e.target.value = "";
    };

    const handleDetailedFloorPlanChange = (e) => {
        e.stopPropagation();
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        setSelectedDetailedFloorPlans((prev) => [...prev, ...files]);
        setDetailedFloorPlanPreviews((prev) => [
            ...prev,
            ...files.map((f) => URL.createObjectURL(f)),
        ]);
        e.target.value = "";
    };

    // Drop a single mis-selected image from the pending upload set. We drive
    // submission from `selectedImages` state (not the native input), so
    // splicing the array + revoking the freed object URL is enough.
    const removeSelectedImage = (index) => {
        setImagePreviews((prev) => {
            const url = prev[index];
            if (url) URL.revokeObjectURL(url);
            return prev.filter((_, i) => i !== index);
        });
        setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    };

    const removeSelectedFloorPlan = (index) => {
        setDetailedFloorPlanPreviews((prev) => {
            const url = prev[index];
            if (url) URL.revokeObjectURL(url);
            return prev.filter((_, i) => i !== index);
        });
        setSelectedDetailedFloorPlans((prev) => prev.filter((_, i) => i !== index));
    };

    // BE requires multipart `detailed_floor_plans_media`. New uploads take
    // precedence; on edit with no new files we re-download the existing ones
    // as Files so the PUT body stays consistent.
    const getDetailedFloorPlanFilesForSubmit = async () => {
        if (selectedDetailedFloorPlans.length > 0) return selectedDetailedFloorPlans;
        if (isEdit && existingDetailedFloorPlans.length > 0) {
            return Promise.all(
                existingDetailedFloorPlans.map(async (item) => {
                    const res = await fetch(item.url);
                    if (!res.ok) throw new Error(`Could not load ${item.key || "floor plan"}`);
                    const blob = await res.blob();
                    return new File([blob], item.key || `floor-plan-${Date.now()}`, {
                        type: item.contentType || blob.type || "application/octet-stream",
                    });
                }),
            );
        }
        return [];
    };

    const handleSubmit = async (data) => {
        // Images can be removed individually now, so the native input's
        // `required` is no longer the source of truth — validate the state
        // array explicitly. Mirror the dropzone's required rule: needed for a
        // new config, or for an edit that has no existing images to fall back on.
        const imagesRequired = !isEdit || existingPropertyImages.length === 0;
        if (imagesRequired && selectedImages.length === 0) {
            toast.error("Please upload at least one image.");
            return;
        }

        let detailedFloorPlanFiles;
        try {
            detailedFloorPlanFiles = await getDetailedFloorPlanFilesForSubmit();
        } catch (e) {
            console.error(e);
            toast.error(e?.message || "Could not prepare detailed floor plans. Try uploading them again.");
            return;
        }
        if (detailedFloorPlanFiles.length === 0) {
            toast.error("Please upload at least one detailed floor plan.");
            return;
        }

        try {
            const fd = new FormData();
            fd.append("project_phase_id", phaseId);
            fd.append("config_name", data.configName);
            if (data.propertyType !== "Shop") fd.append("size_bhk", parseInt(data.sizeBhk, 10));
            fd.append("carpet_area_sqft", parseFloat(data.carpetAreaSqft));
            fd.append("carpet_area_sqmt", parseFloat(data.carpetAreaSqmt || 0));
            fd.append("super_builtup_area_sqft", parseFloat(data.superBuiltupAreaSqft));
            fd.append("super_builtup_area_sqmt", parseFloat(data.superBuiltupAreaSqmt || 0));
            fd.append("dry_balcony_area_sqft", parseFloat(data.dryBalconyAreaSqft || 0));
            fd.append("dry_balcony_area_sqmt", parseFloat(data.dryBalconyAreaSqmt || 0));
            fd.append("balcony_area_sqft", parseFloat(data.balconyAreaSqft || 0));
            fd.append("balcony_area_sqmt", parseFloat(data.balconyAreaSqmt || 0));
            fd.append("base_price", parseFloat(data.basePrice));
            fd.append("currency_id", data.currencyId);
            fd.append("property_type", data.propertyType);

            selectedImages.forEach((img) => fd.append("property_config_images", img));
            detailedFloorPlanFiles.forEach((fp) => fd.append("detailed_floor_plans_media", fp));

            const response = isEdit
                ? await axiosInstance.put(`/property/config?propertyConfigId=${editingPropertyConfigId}`, fd, {
                      headers: { "Content-Type": "multipart/form-data" },
                  })
                : await axiosInstance.post(`/property/config`, fd, {
                      headers: { "Content-Type": "multipart/form-data" },
                  });

            onCreate?.(response);
            toast.success(`Configuration ${isEdit ? "updated" : "added"} successfully`);
            onClose();
        } catch (error) {
            console.error("Error creating property configuration:", error);
            // Pydantic validation errors arrive as an array of {msg, loc}.
            const detail = error?.response?.data?.detail || error?.detail;
            const msg = Array.isArray(detail)
                ? detail.map((d) => d.msg || d.message || JSON.stringify(d)).join(", ")
                : typeof detail === "string"
                    ? detail
                    : error?.response?.data?.message || "Please try again.";
            toast.error(`Failed to ${isEdit ? "update" : "add"} configuration. ${msg}`);
        }
    };

    return (
        <FormModal
            open={isOpen}
            onClose={onClose}
            title={isEdit ? "Edit Configuration" : "Add New Configuration"}
            size="lg"
        >
            <FormProvider {...methods}>
                <form
                    onSubmit={methods.handleSubmit(handleSubmit)}
                    className="space-y-4"
                    noValidate
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field
                            name="propertyType"
                            label="Property Type"
                            type="select"
                            options={PROPERTY_TYPE_OPTIONS}
                            required
                        />
                        {/* Primary measurement unit is UI-only — it just hints which side
                            of the AreaPair to focus first; not submitted to BE. */}
                        <div>
                            <label className="block text-sm font-normal text-slate-500 mb-1">
                                Primary Measurement Unit
                            </label>
                            <select
                                value={primaryMeasurementUnit}
                                onChange={(e) => setPrimaryMeasurementUnit(e.target.value)}
                                className="w-full h-9 px-2 border border-slate-300 rounded-md focus:border-flamingo-500 focus:ring-0 focus:outline-none transition-colors duration-200 text-sm bg-white"
                            >
                                {MEASUREMENT_UNITS.map((u) => (
                                    <option key={u.value} value={u.value}>{u.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field name="configName" label="Configuration Name" required />
                        <SizeBhkField />
                    </div>

                    <AreaPair label="Carpet Area" nameSqft="carpetAreaSqft" nameSqmt="carpetAreaSqmt" required />
                    <AreaPair label="Super Built-up Area" nameSqft="superBuiltupAreaSqft" nameSqmt="superBuiltupAreaSqmt" required />
                    <NonShopFields />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field name="basePrice" label="Base Price" type="number" required />
                        <Field
                            name="currencyId"
                            label="Currency"
                            type="select"
                            options={currencyOptions}
                            required
                        />
                    </div>

                    {isEdit && loadingExistingMedia && (
                        <p className="text-sm text-gray-500 flex items-center gap-2">
                            <Spinner className="h-4 w-4" />
                            Loading existing images and floor plans…
                        </p>
                    )}
                    {isEdit && !loadingExistingMedia && (
                        <p className="text-xs text-gray-500">
                            The file inputs stay empty for security; anything already saved is shown
                            under "Existing Images" / "Existing Detailed Floor Plans" below.
                        </p>
                    )}

                    <FileDropzone
                        label="Upload Images"
                        required={!isEdit || existingPropertyImages.length === 0}
                        multiple
                        accept="image/*"
                        onChange={handleImageChange}
                        selectedCount={selectedImages.length}
                        helper="PNG, JPG, WebP and other image formats"
                        inputKey={`img-${editingPropertyConfigId ?? "new"}`}
                    />
                    <ImageStrip label="Existing Images" items={existingPropertyImages} />
                    {imagePreviews.length > 0 && (
                        <div className="flex flex-wrap gap-3 mt-1">
                            {imagePreviews.map((preview, index) => (
                                <div key={index} className="w-24 h-24 relative group">
                                    <img
                                        src={preview}
                                        alt={`Preview ${index + 1}`}
                                        className="w-full h-full object-cover rounded-md"
                                    />
                                    <RemoveTileButton
                                        onClick={() => removeSelectedImage(index)}
                                        label={`Remove image ${index + 1}`}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    <FileDropzone
                        label="Upload Detailed Floor Plans"
                        required={!isEdit || existingDetailedFloorPlans.length === 0}
                        multiple
                        accept="image/*,.pdf"
                        onChange={handleDetailedFloorPlanChange}
                        selectedCount={selectedDetailedFloorPlans.length}
                        helper="Images or PDF"
                        inputKey={`dfp-${editingPropertyConfigId ?? "new"}`}
                    />
                    <ImageStrip label="Existing Detailed Floor Plans" items={existingDetailedFloorPlans} />
                    {detailedFloorPlanPreviews.length > 0 && (
                        <div className="mt-1">
                            <label className="block text-xs font-medium text-gray-500 mb-2">
                                New Detailed Floor Plans
                            </label>
                            <div className="flex flex-wrap gap-3">
                                {detailedFloorPlanPreviews.map((preview, index) => (
                                    <div key={`new-${index}`} className="w-24 h-24 relative group border rounded-md overflow-hidden">
                                        {selectedDetailedFloorPlans[index]?.type === "application/pdf" ? (
                                            <PdfTile />
                                        ) : (
                                            <img
                                                src={preview}
                                                alt={`Floor Plan Preview ${index + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                        )}
                                        <RemoveTileButton
                                            onClick={() => removeSelectedFloorPlan(index)}
                                            label={`Remove floor plan ${index + 1}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <FormSubmit loadingLabel={isEdit ? "Updating..." : "Submitting..."}>
                        {isEdit ? "Update" : "Submit"}
                    </FormSubmit>
                </form>
            </FormProvider>
        </FormModal>
    );
};

// ─── Local sub-components ────────────────────────────────────────────────

/** Big drag-and-drop file dropzone. Used twice in this modal (images, plans).
 *  Kept in-file because nothing else uses it yet — promote to form/ if more
 *  screens need it. */
const FileDropzone = ({
    label,
    required,
    multiple,
    accept,
    onChange,
    selectedCount,
    helper,
    inputKey,
}) => (
    <div className="w-full border border-gray-200 rounded-lg p-4 bg-gray-50/50">
        <label className="block text-sm font-normal text-slate-500 mb-3">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="relative flex items-center justify-center w-full min-h-[5rem] px-3 transition bg-white border-2 border-gray-300 border-dashed rounded-md cursor-pointer hover:border-gray-400 focus-within:outline-none focus-within:ring-2 focus-within:ring-gray-200">
            <input
                key={inputKey}
                type="file"
                multiple={multiple}
                accept={accept}
                onChange={onChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                required={required}
            />
            <div className="pointer-events-none flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 py-2 text-center">
                <svg className="w-6 h-6 text-gray-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <div>
                    <span className="font-medium text-gray-700 text-sm">Drop files here or browse</span>
                    <p className="text-xs text-gray-500 mt-0.5">{helper}</p>
                </div>
            </div>
        </div>
        {selectedCount > 0 && (
            <p className="text-xs text-gray-600 mt-2">
                {selectedCount} file{selectedCount !== 1 ? "s" : ""} selected
            </p>
        )}
    </div>
);

/** Small circular ✕ overlay shown on a pending-upload tile. Visible on
 *  hover/focus so a mis-selected file can be dropped before submit. */
const RemoveTileButton = ({ onClick, label }) => (
    <button
        type="button"
        onClick={(e) => {
            e.stopPropagation();
            onClick();
        }}
        aria-label={label}
        title="Remove"
        className="absolute -top-1.5 -right-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow-sm opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity hover:bg-red-600"
    >
        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l8 8M14 6l-8 8" />
        </svg>
    </button>
);

const PdfTile = () => (
    <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <div className="text-center">
            <svg className="w-8 h-8 mx-auto mb-1 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <span className="text-xs text-gray-600">PDF</span>
        </div>
    </div>
);

const ImageStrip = ({ label, items }) => {
    if (!items || items.length === 0) return null;
    return (
        <div className="mt-1">
            <label className="block text-xs font-medium text-gray-500 mb-2">{label}</label>
            <div className="flex flex-wrap gap-3">
                {items.map((item, index) => (
                    <div key={`existing-${index}`} className="w-24 h-24 relative border rounded-md overflow-hidden">
                        {item.contentType === "application/pdf" ? (
                            <a href={item.url} target="_blank" rel="noopener noreferrer"
                                className="w-full h-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-colors">
                                <PdfTile />
                            </a>
                        ) : (
                            <a href={item.url} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
                                <img src={item.url} alt={`${label} ${index + 1}`}
                                    className="w-full h-full object-cover hover:opacity-80 transition-opacity" />
                            </a>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CreateConfigModal;
